import { mapKeys, flattenDeep, omit } from 'lodash'
import { UT_Client, apiClient } from 'utils/api-client'
import { getAuthHeader } from 'utils/auth'

import {
  SCAN_NAME_FIELD,
  COOKIES,
  LABELS,
  FLOWS,
  WEBHOOKS,
  RESTRICTIONS_FIELD_NAME,
  FLOW_NAME_FIELD,
  FLOW_ACTIONS,
  WEBHOOK_HEADERS,
  WEBHOOK_HEADER_NAME,
  WEBHOOK_HEADER_VALUE,
  SCAN_TYPE,
  SCAN_TYPE_FULL,
  SCAN_TYPE_CUSTOM,
  SCAN_TYPE_FLOW,
  CUSTOM_SCAN_FIELD,
  RULES,
  RULE_PREFIX,
  RULE_NAME,
  RULE_TRIGGER,
  RULE_ACTION_TYPE,
  SCAN_PREFIX,
  RULE_ACTION_EMAIL_VALUE,
  USER_KEYS,
  COOKIE_KEYS,
  LABEL_KEYS,
  FLOW_KEYS,
  WEBHOOK_KEYS,
  FILTER_KEYS,
  FILTER_NAME,
  FILTER_TYPE,
  FILTER_PROPERTIES,
  FILTER_CONDITION,
  FILTER_VALUE,
  FILTER_PROPERTY_CONDITION,
  FILTER_PROPERTY_TYPE,
  FILTER_PROPERTY_VALUE,
  RULE_WEBHOOK_KEYS,
  RULE_ACTION_TYPE_EMAIL,
  RULE_ACTION_TYPE_WEBHOOK,
  RULE_ACTIONS,
  RULES_KEYS,
  FULL_SCAN_KEYS,
  FLOW_PREFIX,
  FLOW_SCAN_KEYS,
  FILTER_TYPE_SCAN,
  SCAN_PARAMETERS_FLAG_FIELD
} from 'views/ScanSettings/NewScan/constants'
import { formatWebhookHeaders } from './utils';


export function extractItemsByKeys(fields, keys) {
  return fields.filter(c => {
    let objKeys = Object.keys(c)
    return objKeys.every(key => keys.includes(key))
  })
}

export function extractWebhook(webhooks) {
  let headers = {}
  let result = webhooks.map(wh => {
    if (wh[WEBHOOK_HEADER_NAME] && wh[WEBHOOK_HEADER_VALUE]) {
      for (let i = 0, len = wh[WEBHOOK_HEADER_NAME].length; i < len; i++) {
        let headerName = wh[WEBHOOK_HEADER_NAME][i]
        headers[headerName] = wh[WEBHOOK_HEADER_VALUE][i]
      }
    }
    return Object.assign({}, omit(wh, [WEBHOOK_HEADER_NAME, WEBHOOK_HEADER_VALUE]), { [WEBHOOK_HEADERS]: headers })
  })
  return result
}

export function prepareForValidation(fields) {
  const mapFieldKeys = f =>
    mapKeys(f, (value, key) => {
      let keys = key.split('_');
      keys.shift();
      return keys.join("_");
    })
  return Array.isArray(fields) ? fields.map(mapFieldKeys) : mapFieldKeys(fields)
}

export function extractError(error, response, isTypeFlow) {
  // error from server validation

  if (error.response) 
  {
    if(isTypeFlow && error.response.status === 400){
      response.errors = ["The flow has errors, please correct JSON file."];
    }
    else if(error.response.data){
      let _errors = error.response.data.Errors || error.response.data.errors || []
      response.errors = response.errors.concat(flattenDeep(_errors))
    }

    return response

  } else {
    // error from client validation
    return { origin: 'client', errors: flattenDeep(Object.values(error.errors).map(e => e.errors)) }
  }
}

export function extractResponse(response) {
  if (Array.isArray(response)) return response.errors ? response : response.filter(e => e.status !== 200)
  else return response.errors ? response : response
}

export async function remoteValidation(
  fields,
  fieldKeys,
  endpoint,
  response,
  passExtractionByKeys,
  ignoreMappingForValidation,
  isTypeFlow
) {
  let headers = getAuthHeader()
  try {
    if (Array.isArray(fields)) {
      let newItems = passExtractionByKeys ? fields : extractItemsByKeys(fields, fieldKeys)
      if (newItems && newItems.length) {
        let promisesArray = []
        for (let i = 0; i < newItems.length; i += 1) {
          let validateNewItems = prepareForValidation(newItems[i])
          let itemToCall = await apiClient(endpoint, { body: validateNewItems, headers: headers })
          promisesArray.push(itemToCall)
        }
        return await Promise.all(promisesArray)
      }
    } else {
      let validateNewItem = ignoreMappingForValidation ? fields : prepareForValidation(fields)
      let response = await apiClient(endpoint, { body: validateNewItem, headers: headers })
      return response
    }
  } catch (err) {
    return extractError(err, response, isTypeFlow)
  }
}

export const validateBasicInfo = async (form, project = null) => {
  try {
    let failureResponse = { origin: 'server', errors: [] }
    const scanName = await form.validateFields([SCAN_NAME_FIELD])
    let response = await remoteValidation(
      scanName,
      [SCAN_NAME_FIELD],
      `/${project}/scan-settings/validate/name`,
      failureResponse
    )
    return response.errors && !!response.errors.length ? response : null
  } catch (err) {
    return extractError(err)
  }
}

export const validateScan = async (form, project = null, serverRestrictionList) => {
  try {
    let failureResponse = { origin: 'server', errors: [] }
    let response
    const scanType = form.getFieldsValue([SCAN_TYPE])[SCAN_TYPE]
    if (scanType === SCAN_TYPE_FULL) {

      const fullScan = await form.validateFields(FULL_SCAN_KEYS)
      const restrictions = await form.validateFields([RESTRICTIONS_FIELD_NAME])
      if(restrictions && restrictions[RESTRICTIONS_FIELD_NAME]){
        serverRestrictionList = serverRestrictionList.map(r => r.restriction)
        let restrictionsToValidate = { [RESTRICTIONS_FIELD_NAME]: [] }
        restrictions[RESTRICTIONS_FIELD_NAME].forEach(r => {
          !serverRestrictionList.includes(r) && restrictionsToValidate[RESTRICTIONS_FIELD_NAME].push(r)
        })

        response = !!restrictionsToValidate[RESTRICTIONS_FIELD_NAME].length
          ? await remoteValidation(
              restrictionsToValidate,
              [RESTRICTIONS_FIELD_NAME],
              '/global/restrictions/validate',
              failureResponse
            )
          : {}
        response = extractResponse(response)
        failureResponse = response && !!response.length ? response : failureResponse
          
        fullScan[RESTRICTIONS_FIELD_NAME] = restrictions[RESTRICTIONS_FIELD_NAME]
      }
      
      response = await remoteValidation(
        fullScan,
        FULL_SCAN_KEYS,
        `/${project}/scan-settings/validate/full`,
        failureResponse
      )

      response = extractResponse(response)
      failureResponse = response && !!response.length ? response : failureResponse
    }

    if (scanType === SCAN_TYPE_CUSTOM) {
      const customScan = await form.validateFields([CUSTOM_SCAN_FIELD])
      customScan[CUSTOM_SCAN_FIELD] = customScan[CUSTOM_SCAN_FIELD].replace(/^\s*[\r\n]/gm, '').split(/\n/)
      customScan[SCAN_PARAMETERS_FLAG_FIELD] = form.getFieldsValue([SCAN_PARAMETERS_FLAG_FIELD])[SCAN_PARAMETERS_FLAG_FIELD];
      let response = await remoteValidation(
        customScan,
        [CUSTOM_SCAN_FIELD],
        `/${project}/scan-settings/validate/list`,
        failureResponse
      )
      response = extractResponse(response)
      failureResponse = response.errors && !!response.errors.length ? response : failureResponse
    }
    if (scanType === SCAN_TYPE_FLOW) {
      const flowScan = form.getFieldValue(`${SCAN_PREFIX}${FLOWS}`)
      if (!flowScan[0]) {
        failureResponse.errors.push(['Save flow is required'])
      } else {
        let response = {}
        if (flowScan[0].id) {
          let ignoreMappingForValidation = true
          response = await remoteValidation(
            {flow: flowScan[0].id},
            null,
            `/${project}/scan-settings/validate/flow`,
            failureResponse,
            null,
            ignoreMappingForValidation
          )
        } else {
          const newFlow = {
            "new_flow": {
              name: flowScan[0][`${SCAN_PREFIX}${FLOW_NAME_FIELD}`],
              actions: flowScan[0][`${SCAN_PREFIX}${FLOW_ACTIONS}`],
              "enable_parameters": flowScan[0][SCAN_PARAMETERS_FLAG_FIELD]
            }
          }
          response = await remoteValidation(newFlow, FLOW_KEYS, `/${project}/scan-settings/validate/flow`, failureResponse, null , true)
        }
        response = extractResponse(response)
        failureResponse = response && !!response.length ? response : failureResponse
      }
    }
    return !!failureResponse.errors.length ? failureResponse : null
  } catch (err) {
    return extractError(err)
  }
}

export const mapFilterRequest = filters => {
  return filters.map(filter => {
    let mappedFilters = {}
    mappedFilters[FILTER_NAME] = filter[FILTER_NAME]
    mappedFilters[FILTER_TYPE] = FILTER_TYPE_SCAN
    let properties = {}
    properties[FILTER_PROPERTY_TYPE] = filter[FILTER_TYPE]
    properties[FILTER_PROPERTY_CONDITION] = filter[FILTER_CONDITION]
    properties[FILTER_PROPERTY_VALUE] = filter.filter_value
    mappedFilters[FILTER_PROPERTIES] = [properties]
    return mappedFilters
  })
}

export const validateFilters = async filters => {
  try {
    let failureResponse = { origin: 'server', errors: [] }
    let newFilters = filters.filter(f => !f.id)
    let mappedFilters = mapFilterRequest(newFilters)
    let response =
      mappedFilters &&
      !!mappedFilters.length &&
      (await remoteValidation(mappedFilters, FILTER_KEYS, '/global/filters/validate', failureResponse))
    response = extractResponse(response)
    return response && response.errors && !!response.errors.length ? response : null
  } catch (err) {
    return extractError(err)
  }
}


export const validateRule = async (rule, project = null) => {
  try {
    const rules = rule.filter(r => !r.id)
    if (rules && rules.length) {
      let validateRules = rules.map(r => {
        let rule = {}
        rule[RULE_NAME] = r[RULE_NAME]
        rule[RULE_TRIGGER] = r[RULE_TRIGGER];
        rule[RULE_ACTIONS] = [];

        if (r[RULE_TRIGGER] !== 'failure') {
          let newFilters = extractItemsByKeys(r.filters, FILTER_KEYS)
          let userFilters = extractItemsByKeys(r.filters, USER_KEYS)

          if (newFilters && newFilters.length) {
            newFilters = mapFilterRequest(newFilters);
            rule["rule_new_filters"] = prepareForValidation(newFilters);
          }

          if (userFilters && userFilters.length) {
            rule["rule_filters"] = userFilters.map(u => u.id)
          }
        }

        if (r[RULE_ACTION_EMAIL_VALUE]) {
          rule[RULE_ACTIONS].push(
            {
              type: RULE_ACTION_TYPE_EMAIL,
              emails: r[RULE_ACTION_EMAIL_VALUE]
            }
          )
        }

        if (r[`${RULE_PREFIX}${WEBHOOKS}`]) {
          let webhooks = r[`${RULE_PREFIX}${WEBHOOKS}`]
          let newWebhook = extractItemsByKeys(webhooks, RULE_WEBHOOK_KEYS);
          if(newWebhook && !newWebhook.length)
          {
            newWebhook = extractItemsByKeys(webhooks, WEBHOOK_KEYS);
          }

          let userWebhook = extractItemsByKeys(webhooks, USER_KEYS);
          newWebhook && newWebhook.forEach((nw) => {

            nw = formatWebhookHeaders(nw);
            let webHookRequest = prepareForValidation(nw);

            rule[RULE_ACTIONS].push({
              type: `${RULE_ACTION_TYPE_WEBHOOK}`,
              webhook: webHookRequest
            })  
          })

          userWebhook && userWebhook.forEach((w) => {
              rule[RULE_ACTIONS].push({
                  type: RULE_ACTION_TYPE_WEBHOOK,
                  webhook: w.id
              })
          })
        }

        return rule
      })
      let failureResponse = { origin: 'server', errors: [] }
      let response = await remoteValidation(validateRules, RULES_KEYS, '/global/rules/validate', failureResponse, true)
      response = extractResponse(response)
      return response && !!response.errors.length ? response : null
    }
    return null
  } catch (err) {
    //return extractError(err)
  }
}
